/**
 * DocumentationSection — the contributor's help-documentation controls for
 * one arche.
 *
 * Contributors never edit help-article bodies directly: the platform's AI
 * documentation pipeline writes them (auto-generated at every publish).
 * This section exposes the high-level command surface — a "Refresh
 * documentation" button with an optional steering instruction, e.g. "make
 * sure the export feature is fully documented". Generation is queued and
 * billed to the requesting collaborator.
 */

import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

interface DocumentationSectionProps {
  archeId: string;
}

const MAX_INSTRUCTION_CHARS = 1000;

export function DocumentationSection({ archeId }: DocumentationSectionProps) {
  const [instruction, setInstruction] = React.useState('');

  const refreshMutation = useMutation({
    mutationFn: () => api.refreshArcheDocumentation(archeId, instruction.trim() || undefined),
  });

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Help documentation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Your arche&apos;s help articles are written by the Fias documentation AI — they are
          regenerated automatically on every publish, and Fias AI and Support use them to answer
          &ldquo;how does this work?&rdquo; questions about your arche. To improve them, give the AI
          a high-level instruction and refresh.
        </p>
        <textarea
          className="w-full rounded-md border bg-background p-2 text-sm"
          rows={2}
          maxLength={MAX_INSTRUCTION_CHARS}
          placeholder='Optional instruction — e.g. "Make sure the export feature is fully documented"'
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          disabled={refreshMutation.isPending}
        />
        <div className="flex items-center gap-3">
          <Button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            data-testid="refresh-documentation"
          >
            {refreshMutation.isPending ? 'Queueing…' : 'Refresh documentation'}
          </Button>
          {refreshMutation.isSuccess && (
            <span className="text-sm text-muted-foreground">
              Queued — regenerated articles appear within a few minutes.
            </span>
          )}
          {refreshMutation.isError && (
            <span className="text-sm text-destructive">
              {refreshMutation.error instanceof Error
                ? refreshMutation.error.message
                : 'Failed to queue the refresh — try again shortly.'}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
